import { OVERALL_SMOOTH } from "@/config/designer";
import type { VoxelField } from "./VoxelField";
import { regularizeAll } from "./regularize";

export interface OverallSmoothResult {
  applied: boolean;
  changedSamples: number;
  reason?: string;
}

/**
 * Mean-curvature style smoothing over the complete sculpted surface. The flat
 * mounting layer is never touched and every sample remains bounded by the
 * original foam blank, so the operation cannot create material outside it.
 */
export function applyOverallSmooth(
  field: VoxelField,
  requestedPercent: number,
): OverallSmoothResult {
  const strength = clamp(requestedPercent / 100, 0, 1);
  if (strength <= 0) {
    return { applied: false, changedSamples: 0, reason: "Set overall smooth above 0%." };
  }

  const passes = Math.max(1, Math.round(1 + strength * (OVERALL_SMOOTH.maxPasses - 1)));
  const blend = lerp(OVERALL_SMOOTH.minBlend, OVERALL_SMOOTH.maxBlend, strength);
  const { data, dirty, nx, ny, nz } = field;
  const band = field.h * 3.25;
  let source = new Float32Array(data);
  let target = new Float32Array(data.length);
  let changedSamples = 0;

  for (let pass = 0; pass < passes; pass++) {
    target.set(source);
    for (let k = 1; k < nz - 1; k++) {
      for (let j = field.baseCellJ + 2; j < ny - 1; j++) {
        const y = field.sampleY(j);
        for (let i = 1; i < nx - 1; i++) {
          const p = field.index(i, j, k);
          const previous = source[p] as number;
          if (Math.abs(previous) > band) continue;

          const average =
            ((source[field.index(i - 1, j, k)] as number) +
              (source[field.index(i + 1, j, k)] as number) +
              (source[field.index(i, j - 1, k)] as number) +
              (source[field.index(i, j + 1, k)] as number) +
              (source[field.index(i, j, k - 1)] as number) +
              (source[field.index(i, j, k + 1)] as number)) /
            6;
          let next = previous + (average - previous) * blend;
          const blank = field.blankDistance(field.sampleX(i), y, field.sampleZ(k));
          if (next < blank) next = blank;
          next = field.clampValue(next);
          if (Math.abs(next - previous) < 1e-6) continue;
          target[p] = next;
          dirty[p] = 1;
          changedSamples++;
        }
      }
    }
    const swap = source;
    source = target;
    target = swap;
  }

  if (changedSamples === 0) {
    return { applied: false, changedSamples: 0, reason: "The piece is already smooth." };
  }
  data.set(source);
  regularizeAll(field);
  return { applied: true, changedSamples };
}

function clamp(value: number, min: number, max: number): number {
  return value < min ? min : value > max ? max : value;
}

function lerp(a: number, b: number, t: number): number {
  return a + (b - a) * t;
}
